import { LocationOnOutlined, Delete, Edit, Update } from '@mui/icons-material'
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth'
import AddCircleIcon from '@mui/icons-material/AddCircle'
import { Box, Typography, Card, CircularProgress, IconButton } from '@mui/material'
import { useEffect, useState } from 'react'
import type { AnnualPlanTeamMember } from '../../../models/AnnualPlanTeamMember'
import type { AddOrEditAnnualPlanMeetingRequest } from '../../../models/requestObjects/AnnualPlanRequestObjects'
import { BlueButton } from '../../customComponents/buttons/BlueButton'
import { EventDisplay } from '../../customComponents/EventDisplay'
import IconAccordion from '../../customComponents/IconAccordion'
import { IconWithLabel } from '../../customComponents/IconWithLabel'
import { UserDetailsView } from '../../customComponents/user/DetailsView'
import { AddMeetingModal } from './AddMeetingModal'
import { LockedActivityActionTooltip } from './LockedActivityActionTooltip'
import { ThingsToConsiderButton } from './priorityActivities/ThingsToConsiderButton'
import type { AnnualPlanMeeting } from '../../../models/AnnualPlanMeeting'
import { toast } from 'react-toastify'
import {
  AddMeeting,
  DeleteMeeting, GetAnnualPlanMeetings,
  GetAnnualPlanTeam,
  LockActivity,
  UnlockActivity,
  UpdateMeeting
} from '../../../services/AnnualPlanService'
import { PlanOrganizationTaskName, PlanOrganizationTaskStatus } from '../../../models/PlanOrganizationTask'
import { Modal } from '../../customComponents/Modal'
import type { Address } from '../../../models/Address'
import VideocamIcon from '@mui/icons-material/Videocam'
import TrackChangesIcon from '@mui/icons-material/TrackChanges'
import { TrackMeetingModal } from './TrackMeetingModal'

interface Props {
  annualPlanId: number
  readOnly: boolean
  rcnId: number
  showComments?: boolean
  status: string
  color?: string
  onMeetingsLoaded?: (meetings: AnnualPlanMeeting[]) => void
  showTableOnly?: boolean
  isImplementationPhase?: boolean
  trackingReadOnly?: boolean
  budgetOverviewView?: boolean
}

enum ContinueFunctions {
  Add = 'Add',
  Edit = 'Edit',
  Tracking = 'Tracking'
}

export function MeetingSchedules (props: Props): JSX.Element {
  const [showAddMeeting, setShowAddMeeting] = useState(false)
  const [showTrackMeeting, setShowTrackMeeting] = useState(false)
  const [showBudgetModal, setShowBudgetModal] = useState(false)
  const [editingMeeting, setEditingMeeting] = useState<AddOrEditAnnualPlanMeetingRequest | undefined>(undefined)
  const [teamMembers, setTeamMembers] = useState<AnnualPlanTeamMember[]>([])
  const [showConfirmDelete, setShowConfirmDelete] = useState<boolean>(false)
  const [meetings, setMeetings] = useState<AnnualPlanMeeting[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const isImplementationPhase = props.isImplementationPhase ?? false
  const [continueFunction, setContinueFunction] = useState<ContinueFunctions>(ContinueFunctions.Add)

  const fetchTeam = async (): Promise<void> => {
    const team = await GetAnnualPlanTeam(props.annualPlanId)
    setTeamMembers(team)
  }

  const fetchMeetings = async (updateLoadingState = true): Promise<void> => {
    if (updateLoadingState) setIsLoading(true)
    const meetings = await GetAnnualPlanMeetings(props.annualPlanId, props.rcnId, !isImplementationPhase)
    setMeetings(meetings.sort((a, b) => new Date(a.meetingDate).getTime() - new Date(b.meetingDate).getTime()))
    props.onMeetingsLoaded?.(meetings)
    if (updateLoadingState) setIsLoading(false)
  }

  useEffect(() => {
    const fetchData = async (): Promise<void> => {
      if (props.annualPlanId === 0 || props.rcnId === 0) return
      await Promise.all([
        fetchTeam(),
        fetchMeetings(false)
      ])
      setIsLoading(false)
    }

    void fetchData()
  }, [props.annualPlanId, props.rcnId])

  const getComponentKey = (id: string, title: string, isUpdate: boolean, descriptor?: string): string => {
    return `${id}${title}${descriptor ?? ''}${isUpdate ? 'Update' : ''}`
  }

  const addMeetingClick = (): void => {
    if (props.status === PlanOrganizationTaskStatus.NotStarted) {
      toast.error('Set the status to “In Progress” in order to continue.')
      return
    }

    setEditingMeeting(undefined)
    setShowAddMeeting(true)
  }

  const validateBudgetChange = async (isUpdate: boolean, submittedMeeting: AddOrEditAnnualPlanMeetingRequest, isTracking: boolean): Promise<void> => {
    if (submittedMeeting.wasCompleted == null && isTracking) {
      toast.error('Please indicate whether the meeting was held.')
      return
    }

    setEditingMeeting(submittedMeeting)

    const originalBudget = Number(submittedMeeting.budgetRequestAmount)
    const adjustedBudget = Number(submittedMeeting.adjustedBudgetAmount ?? '0')

    let budgetDifference = false

    if (isImplementationPhase && submittedMeeting.isUpdate !== true && originalBudget !== adjustedBudget) {
      if (Number(originalBudget) === 0) {
        budgetDifference = true
      } else if (adjustedBudget !== 0 && originalBudget !== 0 && Math.abs(adjustedBudget - originalBudget) / originalBudget > 0.1) {
        budgetDifference = true
      }
    }

    if (budgetDifference) {
      setContinueFunction(isUpdate ? ContinueFunctions.Edit : ContinueFunctions.Add)
      setShowAddMeeting(false)
      setShowBudgetModal(true)
    } else {
      if (isUpdate) {
        await saveEditedMeeting(submittedMeeting, isTracking)
      } else {
        await saveAddMeeting(submittedMeeting)
      }
    }
  }

  const continueWithBudgetChange = async (): Promise<void> => {
    if (editingMeeting == null) return

    if (continueFunction === ContinueFunctions.Tracking) {
      await saveEditedMeeting(editingMeeting, true)
    } else if (continueFunction === ContinueFunctions.Edit) {
      await saveEditedMeeting(editingMeeting, false)
    } else {
      await saveAddMeeting(editingMeeting)
    }
  }

  const cancelBudgetUpdate = async (): Promise<void> => {
    if (editingMeeting == null) return

    setEditingMeeting(undefined)
    setShowBudgetModal(false)
  }

  async function saveAddMeeting (completedForm: AddOrEditAnnualPlanMeetingRequest): Promise<void> {
    const { success, errors } = await AddMeeting(completedForm)
    if (!success) {
      setShowAddMeeting(true)
      errors.forEach((error) => {
        toast.error(error)
      })
      return
    }
    await refreshAndUnlock('saved')
  }

  async function saveEditedMeeting (completedForm: AddOrEditAnnualPlanMeetingRequest, isTracking: boolean): Promise<void> {
    const { success, errors } = await UpdateMeeting(completedForm)
    if (!success) {
      if (isTracking) {
        setShowTrackMeeting(true)
      } else {
        setShowAddMeeting(true)
      }
      errors.forEach((error) => {
        toast.error(error)
      })
      return
    }
    await refreshAndUnlock('updated')
  }

  const refreshAndUnlock = async (closeReason: string): Promise<void> => {
    if (showAddMeeting) {
      setShowAddMeeting(false)
    }
    if (showTrackMeeting) {
      setShowTrackMeeting(false)
    }
    if (showBudgetModal) {
      setShowBudgetModal(false)
    }

    if (closeReason === 'canceled') {
      await UnlockActivity(editingMeeting?.id ?? 0,
        PlanOrganizationTaskName.MeetingSchedule,
        editingMeeting?.isUpdate ?? false)
      return
    }

    void fetchMeetings()
    if (closeReason === 'saved') {
      toast.success('Meeting Data Saved.')
    } else if (closeReason === 'updated') {
      toast.success('Meeting Data Updated.')
    } else if (closeReason === 'tracked') {
      toast.success('Meeting Data Tracked.')
    } else {
      toast.success('Meeting deleted.')
    }

    await UnlockActivity(editingMeeting?.id ?? 0,
      PlanOrganizationTaskName.MeetingSchedule,
      editingMeeting?.isUpdate ?? false)
  }

  const trackMeeting = async (meeting: AnnualPlanMeeting): Promise<void> => {
    if (meeting?.id != null) {
      const editMeeting: AddOrEditAnnualPlanMeetingRequest = getMeetingRequestFromMeeting(meeting)
      setEditingMeeting(editMeeting)
      setShowTrackMeeting(true)
      await LockActivity(meeting.id ?? 0, PlanOrganizationTaskName.MeetingSchedule, meeting.isUpdate ?? false)
    }
  }

  const updateMeeting = async (meeting: AnnualPlanMeeting): Promise<void> => {
    if (meeting?.id != null) {
      const editMeeting: AddOrEditAnnualPlanMeetingRequest = getMeetingRequestFromMeeting(meeting)

      setEditingMeeting(editMeeting)
      setShowAddMeeting(true)
      await LockActivity(meeting.id ?? 0, PlanOrganizationTaskName.MeetingSchedule, meeting.isUpdate ?? false)
    }
  }

  function getMeetingRequestFromMeeting (meeting: AnnualPlanMeeting): AddOrEditAnnualPlanMeetingRequest {
    return {
      annualPlanId: props.annualPlanId,
      orgId: props.rcnId,
      title: meeting.title,
      description: meeting.description,
      meetingDate: meeting.meetingDate,
      meetingEndDate: meeting.meetingEndDate ?? null,
      location: meeting.address,
      primaryContactId: meeting.primaryContact?.id ?? undefined,
      budgetRequestAmount: meeting.budgetRequestAmount?.toString() ?? '',
      id: meeting.id,
      isVirtual: meeting.isVirtual,
      virtualLink: meeting.virtualLink,
      isUpdate: meeting.isUpdate ?? false,
      isImplementationPhase,
      rcnOperationId: meeting.rcnOperationId ?? 0,
      wasCompleted: meeting.wasCompleted ?? null,
      meetingScheduleId: meeting.meetingScheduleId,
      actualBudgetAmount: meeting.actualBudgetAmount?.toString() ?? '',
      adjustedBudgetAmount: meeting.adjustedBudgetAmount?.toString() ?? ''
    }
  }

  const cancelEdit = async (): Promise<void> => {
    setShowAddMeeting(false)
    if (editingMeeting == null) return
    await UnlockActivity(editingMeeting?.id ?? 0,
      PlanOrganizationTaskName.MeetingSchedule,
      editingMeeting?.isUpdate ?? false)
    setEditingMeeting(undefined)
  }

  const startDeleteMeeting = (meeting: AnnualPlanMeeting): void => {
    setEditingMeeting(getMeetingRequestFromMeeting(meeting))
    setShowConfirmDelete(true)
  }

  const continueDeleteMeeting = async (): Promise<void> => {
    setShowConfirmDelete(false)
    if (editingMeeting?.id != null) {
      await DeleteMeeting(editingMeeting.id ?? 0, editingMeeting.isUpdate ?? false)
    }
    await refreshAndUnlock('deleted')
  }

  const cancelDeleteMeeting = async (): Promise<void> => {
    setShowConfirmDelete(false)
    await refreshAndUnlock('deleted')
  }
  const stringFromAddress = (adr: Address): string => {
    if (adr.city === '') {
      return adr.street
    }

    const addressString = `${adr.street}${adr.apartment == null ? '' : ` ${adr.apartment}`} ${adr.city}, ${adr.state} ${adr.zip}`
    return addressString
  }

  const getImplementationOptions = (meeting: AnnualPlanMeeting): JSX.Element | null => {
    if (props.readOnly && props.trackingReadOnly === true) return null
    if (props.trackingReadOnly !== true && props.readOnly) {
      return <>
        <LockedActivityActionTooltip lock={meeting.activityLock}>
          <IconButton sx={{ color: 'var(--grey-700)' }} disabled={meeting.isUpdate !== true} onClick={async () => { await trackMeeting(meeting) }} data-testid={getComponentKey(meeting.id?.toString() ?? '', 'TrackMeetingButton', meeting.isUpdate ?? false)} aria-label='Track Button'>
            <TrackChangesIcon />
          </IconButton>
        </LockedActivityActionTooltip>
      </>
    }

    return <>
      <LockedActivityActionTooltip lock={meeting.activityLock}>
        <IconButton sx={{ color: 'var(--grey-700)' }} onClick={async () => { await updateMeeting(meeting) }} data-testid={getComponentKey(meeting.id?.toString() ?? '', 'EditMeetingButton', meeting.isUpdate ?? false)} aria-label='edit meeting'>
          <Update />
        </IconButton>
        <IconButton sx={{ color: 'var(--grey-700)' }} disabled={meeting.isUpdate !== true} onClick={async () => { await trackMeeting(meeting) }} data-testid={getComponentKey(meeting.id?.toString() ?? '', 'TrackMeetingButton', meeting.isUpdate ?? false)} aria-label='Track Button'>
          <TrackChangesIcon />
        </IconButton>
        <IconButton sx={{ color: 'var(--grey-700)' }} onClick={() => { startDeleteMeeting(meeting) }} data-testid={getComponentKey(meeting.id?.toString() ?? '', 'DeleteMeetingButton', meeting.isUpdate ?? false)} aria-label='delete meeting'>
          <Delete />
        </IconButton>
      </LockedActivityActionTooltip>
    </>
  }

  return <Box display='flex' flexDirection='column' gap='1em' data-testid='meetingSchedules'>
    <Typography variant='h4' color={props.color}>Meetings</Typography>

    {props.showTableOnly !== true && (
      <Box sx={{ width: '100%', alignItems: 'center' }}>
        {!props.readOnly &&
          <BlueButton onClick={addMeetingClick} dataTestId='addMeetingButton'>
            Add Meeting <AddCircleIcon sx={{ ml: '0.5em' }} />
          </BlueButton>
    }

        <Box sx={{ float: 'right' }}>
          <ThingsToConsiderButton
            buttonDataTestId='rcnOperationsThingsToConsiderButton'
            thingsToConsiderSections={[]}
            cardDataTestId='rcnOperationsThingsToConsiderCard'
          />
        </Box>
      </Box>
    )}

    <Card data-testid='cardContainer' sx={{ minHeight: '400px', width: '100%', display: meetings.length === 0 ? 'grid' : 'block' }}>
      {isLoading && <CircularProgress />}

      {!isLoading && meetings.length === 0 &&
        <Box sx={{ justifySelf: 'center', alignSelf: 'center', width: '300px' }}>
          <Box sx={{ justifySelf: 'center', mb: '.75em' }}>
            <CalendarMonthIcon sx={{ color: 'var(--blue-300)', width: '150px', height: '150px' }} />
            <Typography variant='h4'>No Meetings Added</Typography>
          </Box>
          {!props.readOnly &&
            <Typography variant='body1' sx={{ fontSize: '16px' }}>Use the Add Meeting button to add a Meeting to the schedule.</Typography>}
        </Box>
      }

      {!isLoading && meetings.length > 0 && meetings.map((meeting) => {
        let color = 'none'
        if (meeting.needsReview === true && props.budgetOverviewView === true) {
          color = 'var(--blue-500)'
        } else if (meeting.isUpdate === true && props.budgetOverviewView !== true) {
          color = 'var(--grey-300)'
        }
        return <Box data-testid={getComponentKey(meeting.id?.toString() ?? '', meeting.title, meeting.isUpdate ?? false)} key={getComponentKey(meeting.id?.toString() ?? '', meeting.title, meeting.isUpdate ?? false)} sx={{ width: '100%', mr: '1em' }}>
          <IconAccordion
            color={color}
            colorWidth='.5em'
            baseStyle={{ m: '1em' }}
            dataTestId={getComponentKey(meeting.id?.toString() ?? '', meeting.title, meeting.isUpdate ?? false, 'accordion')}
            expanded={false}
            expandButtonKey={getComponentKey(meeting.id?.toString() ?? '', meeting.title, meeting.isUpdate ?? false, 'expand')}
            accordionSummary={
              <Box sx={{ display: 'grid', alignItems: 'center', my: '.5em' }}>
                <EventDisplay
                  eventBoxStyle={{ width: '2.25em', height: '3.375em', my: '.5em' }}
                  description={meeting.description}
                  budgetRequestAmount={meeting.adjustedBudgetAmount ?? meeting.budgetRequestAmount}
                  actualBudgetAmount={meeting.actualBudgetAmount}
                  event={{
                    title: meeting.title,
                    date: meeting.meetingDate,
                    startTime: meeting.meetingDate,
                    endTime: meeting.meetingEndDate ?? undefined
                  }}
                />
                <Typography variant='body1' sx={{ fontSize: '.75em' }}>{}</Typography>
              </Box>
          }
            showOptions
            optionsElement={
              <Box>
                {!props.readOnly && !isImplementationPhase &&
                  <LockedActivityActionTooltip lock={meeting.activityLock}>
                    <IconButton onClick={async () => { await updateMeeting(meeting) }} data-testid={getComponentKey(meeting.id?.toString() ?? '', 'EditMeetingButton', meeting.isUpdate ?? false)} aria-label='edit meeting'>
                      <Edit sx={{ width: '20px', height: '20px', color: 'var(--grey-700)' }} />
                    </IconButton>
                    <IconButton onClick={() => { startDeleteMeeting(meeting) }} data-testid={getComponentKey(meeting.id?.toString() ?? '', 'DeleteMeetingButton', meeting.isUpdate ?? false)} aria-label='delete meeting'>
                      <Delete sx={{ width: '20px', height: '20px', color: 'var(--grey-700)' }} />
                    </IconButton>
                  </LockedActivityActionTooltip>
                }
                {
                  isImplementationPhase && getImplementationOptions(meeting)
                }
              </Box>
          }
            accordionDetails={
              <Box sx={{ ml: '2em' }}>
                {meeting.actualBudgetAmount != null && (
                  <Typography variant='body1'>Actual Budget: ${meeting.actualBudgetAmount.toLocaleString()}</Typography>
                )}
                {meeting.wasCompleted != null && (
                  <Typography sx={{ mb: '1em' }} variant='body1'>Meeting Was Held: ${meeting.wasCompleted ? 'Yes' : 'No'}</Typography>
                )}
                { meeting.isVirtual &&
                  <IconWithLabel
                    sx={{ mb: '.75em' }}
                    labelAsLink
                    icon={<VideocamIcon />}
                    labelText={meeting.virtualLink}
                  />
              }

                { meeting.address.street !== '' &&
                  <IconWithLabel
                    icon={<LocationOnOutlined />}
                    whiteSpace='normal'
                    labelText={stringFromAddress(meeting.address)}
                    dataTestId='address'
                  />
              }
                { meeting.primaryContact !== null &&
                  <Box sx={{ width: '50%', mt: '.5em' }}>
                    <UserDetailsView user={meeting.primaryContact} allowEdit={false} />
                  </Box>
              }
              </Box>
          }
          />
        </Box>
      })}

      {showConfirmDelete &&
        <Modal
          title='Are you sure?'
          onClose={cancelDeleteMeeting}
          open={true}
          onConfirm={continueDeleteMeeting}
          bodyContent='You are about to delete this item.  This action will prompt a review from the START team.  Are you sure you wish to continue?'
          confirmButtonText='Yes'
          cancelButtonText='No'
        />
    }
    </Card>

    {showAddMeeting &&
      <AddMeetingModal
        teamMembers={teamMembers}
        editMeeting={editingMeeting}
        orgId={props.rcnId}
        annualPlanId={props.annualPlanId}
        isImplementationPhase={isImplementationPhase}
        onCancel={cancelEdit}
        onClose={validateBudgetChange}
      />
    }

    {showTrackMeeting && editingMeeting != null &&
      <TrackMeetingModal
        meeting={editingMeeting}
        onClose={validateBudgetChange}
        onCancel={() => { setShowTrackMeeting(false) }}
      />
    }

    {showBudgetModal &&
      <Modal
        open={true}
        maxWidth='sm'
        title={'Budget Change Exceeds Threshold'}
        confirmButtonText='Save'
        bodyContent={<>
          <Typography variant='body1'>Any individual or cumulative budget update that changes the allocation of contract funds within budget categories by more than 10% requires approval from START.</Typography>
          <Typography variant='body1' sx={{ fontWeight: 'bold' }}>Your budget revisions exceed this 10% threshold.</Typography>
          <Typography variant='body1'>Please confirm and submit your updates, or cancel to make further adjustments before proceeding.</Typography>
        </>}
        onClose={cancelBudgetUpdate}
        onConfirm={continueWithBudgetChange}
      />
    }
  </Box>
}
